import { supabase } from '@/integrations/supabase/client';

interface ErrorContext {
  userId?: string;
  userEmail?: string;
  userRole?: string;
  schoolId?: string;
  academicYear?: string;
  sessionId?: string;
}

interface UserJourney {
  timestamp: string;
  action: string;
  component?: string;
  url?: string;
  data?: any;
}

interface ErrorMetadata {
  component?: string;
  feature?: string;
  errorInfo?: any;
  customData?: any;
}

class ErrorTracker {
  private context: ErrorContext = {};
  private journey: UserJourney[] = [];
  private sessionId: string;
  private isInitialized = false;
  
  constructor() {
    this.sessionId = this.generateSessionId();
    this.setupGlobalErrorHandlers();
  }
  
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  
  private setupGlobalErrorHandlers() {
    // Handle JavaScript runtime errors
    window.addEventListener('error', (event) => {
      this.captureError(event.error, {
        component: 'Global',
        feature: 'Runtime',
        customData: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          message: event.message
        }
      });
    });
    
    // Handle Promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(new Error(event.reason), {
        component: 'Global',
        feature: 'Promise',
        customData: { reason: event.reason }
      });
    });
    
    // Handle React errors via window.reportError if available
    if ('reportError' in window) {
      const originalReportError = window.reportError;
      window.reportError = (error: Error) => {
        this.captureError(error, {
          component: 'React',
          feature: 'Boundary'
        });
        originalReportError(error);
      };
    }
  }
  
  initialize(initialContext?: ErrorContext) {
    if (this.isInitialized) return;
    
    if (initialContext) {
      this.updateContext(initialContext);
    }
    
    // Track session start
    this.addToJourney('session_start', 'System', window.location.href);
    
    this.isInitialized = true;
  }
  
  updateContext(newContext: Partial<ErrorContext>) {
    this.context = { ...this.context, ...newContext };
  }
  
  addToJourney(action: string, component?: string, url?: string, data?: any) {
    const journeyItem: UserJourney = {
      timestamp: new Date().toISOString(),
      action,
      component,
      url: url || window.location.href,
      data
    };
    
    this.journey.push(journeyItem);
    
    // Keep only the last 50 items to avoid memory bloat
    if (this.journey.length > 50) {
      this.journey.shift();
    }
  }
  
  trackPageView(pathname: string, additionalData?: any) {
    this.addToJourney('page_view', 'Navigation', pathname, additionalData);
  }
  
  trackAction(action: string, component?: string, data?: any) {
    this.addToJourney(action, component, undefined, data);
  }
  
  async captureError(error: Error, metadata?: ErrorMetadata) {
    try {
      const errorData = {
        error_id: this.generateErrorId(error),
        error_type: this.categorizeError(error),
        error_name: error.name,
        error_message: error.message,
        error_stack: error.stack,
        error_code: (error as any).code,
        status_code: (error as any).status,
        
        // User context
        user_id: this.context.userId || null,
        user_email: this.context.userEmail || null,
        user_role: this.context.userRole || null,
        school_id: this.context.schoolId || null,
        
        // Session info
        session_id: this.sessionId,
        session_duration: this.getSessionDuration(),
        
        // Application context
        app_version: process.env.REACT_APP_VERSION || 'unknown',
        environment: process.env.NODE_ENV || 'development',
        academic_year: this.context.academicYear || null,
        feature: metadata?.feature || null,
        component: metadata?.component || null,
        
        // Technical details
        url: window.location.href,
        http_method: 'GET', // Default for client-side errors
        user_agent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        screen_info: {
          width: screen.width,
          height: screen.height,
          colorDepth: screen.colorDepth,
          pixelDepth: screen.pixelDepth
        },
        
        // Journey data
        breadcrumbs: this.journey.slice(-20), // Last 20 actions
        last_actions: this.journey.slice(-5), // Last 5 actions
        error_count: 1,
        
        // Metadata
        fingerprint: this.generateFingerprint(error),
        tags: this.generateTags(error, metadata),
        custom_data: {
          ...metadata?.customData,
          errorInfo: metadata?.errorInfo,
          timestamp: Date.now()
        },
        
        // Timestamps
        occurred_at: new Date().toISOString()
      };
      
      const { error: supabaseError } = await supabase
        .from('error_logs')
        .insert(errorData);
      
      if (supabaseError) {
        console.error('Failed to log error to database:', supabaseError);
        // Fallback to console logging
        console.error('Original error:', error, metadata);
      }
      
    } catch (captureError) {
      console.error('Error in error tracking:', captureError);
      console.error('Original error:', error, metadata);
    }
  }
  
  private getSessionDuration(): number {
    const sessionStart = this.journey.find(item => item.action === 'session_start');
    if (!sessionStart) return 0;
    
    return Date.now() - new Date(sessionStart.timestamp).getTime();
  }
  
  private generateErrorId(error: Error): string {
    // Create a unique ID for this specific error occurrence
    return `${error.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  
  private generateFingerprint(error: Error): string {
    // Create a fingerprint for grouping similar errors
    const key = `${error.name}:${error.message}:${this.getStackTraceLines(error.stack).slice(0, 3).join('|')}`;
    return btoa(key).replace(/[^a-zA-Z0-9]/g, '').substr(0, 32);
  }
  
  private getStackTraceLines(stack?: string): string[] {
    if (!stack) return [];
    return stack.split('\n')
      .filter(line => line.trim().length > 0)
      .map(line => line.trim());
  }
  
  private categorizeError(error: Error): string {
    const message = error.message.toLowerCase();
    const stack = (error.stack || '').toLowerCase();
    
    // Network errors
    if (message.includes('fetch') || message.includes('network') || message.includes('connection')) {
      return 'network_error';
    }
    
    // API errors
    if (message.includes('api') || message.includes('request') || message.includes('response')) {
      return 'api_error';
    }
    
    // Authentication errors
    if (message.includes('auth') || message.includes('unauthorized') || message.includes('forbidden')) {
      return 'auth_error';
    }
    
    // Permission errors
    if (message.includes('permission') || message.includes('access denied')) {
      return 'permission_error';
    }
    
    // Database errors
    if (message.includes('database') || message.includes('sql') || message.includes('constraint')) {
      return 'db_query_error';
    }
    
    // Validation errors
    if (message.includes('validation') || message.includes('invalid') || message.includes('required')) {
      return 'validation_error';
    }
    
    // React/component errors
    if (stack.includes('react') || message.includes('component') || message.includes('render')) {
      return 'component_error';
    }
    
    // Memory errors
    if (message.includes('memory') || message.includes('heap') || message.includes('out of memory')) {
      return 'memory_error';
    }
    
    // Timeout errors
    if (message.includes('timeout') || message.includes('time out')) {
      return 'timeout_error';
    }
    
    // Script errors
    if (error.name === 'SyntaxError' || error.name === 'ReferenceError' || error.name === 'TypeError') {
      return 'script_error';
    }
    
    return 'unknown_error';
  }
  
  private generateTags(error: Error, metadata?: ErrorMetadata): string[] {
    const tags: string[] = [];
    
    // Add component tag
    if (metadata?.component) {
      tags.push(`component:${metadata.component}`);
    }
    
    // Add feature tag
    if (metadata?.feature) {
      tags.push(`feature:${metadata.feature}`);
    }
    
    // Add user role tag
    if (this.context.userRole) {
      tags.push(`role:${this.context.userRole}`);
    }
    
    // Add environment tag
    tags.push(`env:${process.env.NODE_ENV || 'development'}`);
    
    // Add browser tag
    const browserInfo = this.getBrowserInfo();
    if (browserInfo) {
      tags.push(`browser:${browserInfo}`);
    }
    
    return tags;
  }
  
  private getBrowserInfo(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'chrome';
    if (ua.includes('Firefox')) return 'firefox';
    if (ua.includes('Safari')) return 'safari';
    if (ua.includes('Edge')) return 'edge';
    return 'unknown';
  }
  
  // Public methods for manual error reporting
  reportError(error: Error | string, metadata?: ErrorMetadata) {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    this.captureError(errorObj, metadata);
  }
  
  reportApiError(endpoint: string, status: number, response?: any) {
    const error = new Error(`API Error: ${status} on ${endpoint}`);
    (error as any).status = status;
    
    this.captureError(error, {
      component: 'API',
      feature: 'Request',
      customData: {
        endpoint,
        status,
        response
      }
    });
  }
  
  reportValidationError(field: string, value: any, rule: string) {
    const error = new Error(`Validation failed: ${field} - ${rule}`);
    
    this.captureError(error, {
      component: 'Validation',
      feature: 'Form',
      customData: {
        field,
        value,
        rule
      }
    });
  }
}

// Create singleton instance
const errorTracker = new ErrorTracker();

// Export functions
export const initializeErrorTracking = (context?: ErrorContext) => {
  errorTracker.initialize(context);
};

export const updateErrorContext = (context: Partial<ErrorContext>) => {
  errorTracker.updateContext(context);
};

export const trackPageView = (pathname: string, additionalData?: any) => {
  errorTracker.trackPageView(pathname, additionalData);
};

export const trackAction = (action: string, component?: string, data?: any) => {
  errorTracker.trackAction(action, component, data);
};

export const logError = (error: Error | string, metadata?: ErrorMetadata) => {
  errorTracker.reportError(error, metadata);
};

export const logApiError = (endpoint: string, status: number, response?: any) => {
  errorTracker.reportApiError(endpoint, status, response);
};

export const logValidationError = (field: string, value: any, rule: string) => {
  errorTracker.reportValidationError(field, value, rule);
};

// Export the tracker instance for advanced usage
export { errorTracker };